import { Component } from 'react';
import { get, flowRight as compose } from 'lodash';
import PropTypes from 'prop-types';
import { graphql } from '@apollo/client/react/hoc';
import {
  MUTATION_UPDATE_FEATURE_ORG_VIEW,
  MUTATION_UPDATE_FEATURE_ORG_SORT_BY,
  MUTATION_UPDATE_FEATURE_ORG_SEARCH,
  MUTATION_RESET_FEATURE_ORG_SEARCH,
  QUERY_FEATURE_ORG_CRITERIA,
  MUTATION_ACTIVATE_FEATURE_FLAG_MAPPING
} from './gql';
import { ReactChildrenPropType } from '../../../../lib/commonPropTypes';

const updateToggle = (toggle) => {
  localStorage.setItem('featureOrg.toggle', toggle);
};

class FeatureFlagOrgsContainer extends Component {
  constructor() {
    super();

    this.getSetting = this.getSetting.bind(this);
    this.resetSearch = this.resetSearch.bind(this);
    this.updatePagination = this.updatePagination.bind(this);
    this.updateSearch = this.updateSearch.bind(this);
    this.updateSortBy = this.updateSortBy.bind(this);
    this.updateView = this.updateView.bind(this);
    this.activateFeatureFlagMapping = this.activateFeatureFlagMapping.bind(this);
  }

  getSetting(setting, fallback) {
    const { [setting]: existing } = this.props;

    const storage = localStorage.getItem(`featureOrg.${setting}`);

    return storage || existing || fallback;
  }

  resetSearch = () => {
    const { resetSearch } = this.props;

    return resetSearch();
  };

  updatePagination = (pagination) => {
    const existing = JSON.parse(this.getSetting('pagination', '{}'));

    localStorage.setItem('featureOrg.pagination', JSON.stringify({ ...existing, ...pagination }));
  };

  updateSearch = (search) => {
    const { updateSearch } = this.props;

    localStorage.setItem('featureOrg.search', search);

    return updateSearch({ variables: { search } });
  };

  updateSortBy = (sortBy) => {
    const { updateSortBy } = this.props;

    localStorage.setItem('featureOrg.sortBy', sortBy);

    return updateSortBy({ variables: { sortBy } });
  };

  updateView = (view) => {
    const { updateView } = this.props;
    return updateView({ variables: { view } });
  };

  activateFeatureFlagMapping = (organizationId, featureFlagId, active) => {
    const { activateFeatureFlagMapping } = this.props;
    return activateFeatureFlagMapping({
      variables: {
        featureFlagId,
        organizationId,
        active
      }
    });
  };

  buildProps = () => ({
    pagination: JSON.parse(this.getSetting('pagination', '{}')),
    search: this.getSetting('search'),
    sortBy: this.getSetting('sortBy'),
    toggle: this.getSetting('toggle'),
    view: this.getSetting('view'),
    resetSearch: this.resetSearch,
    updatePagination: this.updatePagination,
    updateSearch: this.updateSearch,
    updateSortBy: this.updateSortBy,
    updateToggle,
    updateView: this.updateView,
    activateFeatureFlagMapping: this.activateFeatureFlagMapping
  });

  render() {
    const { children, ...otherProps } = this.props;
    return children(this.buildProps(otherProps));
  }
}

FeatureFlagOrgsContainer.propTypes = {
  children: ReactChildrenPropType.isRequired,
  resetSearch: PropTypes.func.isRequired,
  updateSearch: PropTypes.func.isRequired,
  updateSortBy: PropTypes.func.isRequired,
  updateView: PropTypes.func.isRequired,
  pagination: PropTypes.string,
  search: PropTypes.string.isRequired,
  sortBy: PropTypes.string.isRequired,
  toggle: PropTypes.string,
  view: PropTypes.string.isRequired,
  activateFeatureFlagMapping: PropTypes.func.isRequired
};

FeatureFlagOrgsContainer.defaultProps = {
  pagination: '{}',
  toggle: undefined
};

const FeatureFlagOrgsContainerWithMutations = compose(
  graphql(MUTATION_UPDATE_FEATURE_ORG_VIEW, { name: 'updateView' }),
  graphql(MUTATION_UPDATE_FEATURE_ORG_SORT_BY, { name: 'updateSortBy' }),
  graphql(MUTATION_UPDATE_FEATURE_ORG_SEARCH, { name: 'updateSearch' }),
  graphql(MUTATION_RESET_FEATURE_ORG_SEARCH, { name: 'resetSearch' }),
  graphql(MUTATION_ACTIVATE_FEATURE_FLAG_MAPPING, { name: 'activateFeatureFlagMapping' }),
  graphql(QUERY_FEATURE_ORG_CRITERIA, {
    options: () => ({
      fetchPolicy: 'cache-first',
      ssr: false // Authentication only works client side
    }),
    props: ({ data: { featureOrgCriteria } }) => ({
      pagination: get(featureOrgCriteria, 'paginationFeatureOrg', '{}'),
      search: get(featureOrgCriteria, 'searchFeatureOrg', ''),
      sortBy: get(featureOrgCriteria, 'sortByFeatureOrg', ''),
      toggle: get(featureOrgCriteria, 'toggleFeatureOrg', ''),
      view: get(featureOrgCriteria, 'viewFeatureOrg', '')
    })
  })
)(FeatureFlagOrgsContainer);

export default FeatureFlagOrgsContainerWithMutations;
